import { tinymce } from '../../helpers';
import { OutputReferenceHelper, IdHelper, BrowserHelper } from '../../helpers';

let outputReferencePlugin = (editor, url) => {

    let searchInputTimeout;
    let lastKeyCode;
    let currentSearchNode;

    let searchInputDebounce = (selectedNode) => {

        if (searchInputTimeout) {
            clearTimeout(searchInputTimeout);
        }

        searchInputTimeout = setTimeout(() => {
            if (currentSearchNode) {
                OutputReferenceHelper.outputReferenceSearch(selectedNode.textContent, currentSearchNode, editor, 
                    editor.plugins.outputReference ? editor.plugins.outputReference.outputLookupContext : null );
            }
        }, 250);

    };

    let removeToken = (selectedNode) => {
        
        if (BrowserHelper.detectIE()) {

            let wrapper = selectedNode.parentElement;
            let parent = wrapper.parentElement;
            let textNode = document.createTextNode(' ');
            parent.replaceChild(textNode, wrapper);
            moveToTextNode(editor, textNode, 0);
        }
        else {
            let parent = selectedNode.parentElement;
            parent.removeChild(selectedNode);
        }
        
        currentSearchNode = null;
        OutputReferenceHelper.clearOutputReferenceSearch();
    }

    editor.on('focus', (e) => {
        OutputReferenceHelper.setFocusedEditorId(editor.id);
    });

    editor.on('click', (e) => {

        //reset lastKeyCode
        lastKeyCode = null;

    });

    editor.on('keyup', (e) => {

        let selectedNode = editor.selection.getNode();
       
        if (OutputReferenceHelper.isOutputReference(selectedNode)) {
            
            searchInputDebounce(selectedNode);
            
        }

        else if (BrowserHelper.detectIE() && (e.keyCode === 8 || e.keyCode === 46)) { //backspace or delete
            //remove contenteditable for fixed outputs that didn't get deleted
            let fixedTokens = OutputReferenceHelper.getFixedOutputs(editor);
            for (let i = 0; i < fixedTokens.length; i++) {
                fixedTokens[i].removeAttribute('contenteditable');
            }
        }

        else if (e.keyCode === 222 || (e['DOM_VK_HASH'] && lastKeyCode === e['DOM_VK_HASH'])) {

            let editorContent = editor.getContent();

            //if two consecutive hashes exist in the editor content
            let tokenIndex = editorContent.indexOf('##');
            if (tokenIndex >= 0) {

                let wrapper = document.createElement('span');
                let searchNodeId = IdHelper.newGuid();
                let searchNode = document.createElement('span');

                searchNode.setAttribute('contenteditable', 'true');
                searchNode.setAttribute('id', searchNodeId);
                searchNode.setAttribute('class', 'output-token' + (BrowserHelper.detectIE() ? ' ie' : ''));
                searchNode.appendChild(document.createTextNode(BrowserHelper.detectIE() ? '\u200B' : '#'));
                if (BrowserHelper.detectIE()) {
                    wrapper.setAttribute('id', searchNodeId + '_wrapper');
                    wrapper.setAttribute('class', 'output-wrapper');
                    wrapper.setAttribute('contenteditable', 'false');
                    wrapper.appendChild(searchNode);
                }

                //remove those two hashes and place the search box there
                editor.setContent(editorContent.replace('##', BrowserHelper.detectIE() ? wrapper.outerHTML : searchNode.outerHTML));

                let elem = editor.contentDocument.getElementById(searchNodeId);
                setCursor(editor, elem.hasChildNodes ? elem.firstChild : elem, false);
                elem.focus();

                OutputReferenceHelper.registerEventHandlers(editor);
            }

        }

    });

    editor.on('keydown', (e) => {

        let selectedNode = editor.selection.getNode();

        lastKeyCode = e.keyCode;

        if (BrowserHelper.detectIE() && (lastKeyCode === 8 || lastKeyCode === 46)) { //backspace or delete
			/* set fixed tokens to contenteditable to false, so if any get deleted
			by the delete or backspace operation, then the whole token will be deleted and not just
			a character or the delete button within it */
            let fixedTokens = OutputReferenceHelper.getFixedOutputs(editor);
            for (let i = 0; i < fixedTokens.length; i++) {
                fixedTokens[i].setAttribute('contenteditable', 'false');
            }
        }

        if (OutputReferenceHelper.isFixedOutputReference(selectedNode)) {
            //prevent all key presses for tokens, except for arrow keys
            if (lastKeyCode < 37 || lastKeyCode > 40) {
                e.preventDefault();
                return false;
            }
        }
        else if (OutputReferenceHelper.isOutputReference(selectedNode)) {

            if (lastKeyCode === 222 || (e['DOM_VK_HASH'] && lastKeyCode === e['DOM_VK_HASH'])) { //#
                e.preventDefault();
            }

            if (lastKeyCode === 40 || lastKeyCode === 9) { //ArrowDown | Tab
                OutputReferenceHelper.nextFocusedOutputKey();
                e.preventDefault();
            }

            if (lastKeyCode === 38) { //ArrowUp
                OutputReferenceHelper.previousFocusedOutputKey();
                e.preventDefault();
            }

            if (lastKeyCode === 13) { //Enter
                OutputReferenceHelper.selectFocusedOutputKey();
                e.preventDefault();
            }

            if (lastKeyCode === 39) { //ArrowRight

                let range = editor.selection.getRng();
                let length = range.startContainer.textContent.length;
                let offset = range.startOffset;

                if (
                    (length === offset) ||
                    (BrowserHelper.detectIE() && length === 1 && offset === 0)
                ) {
                    OutputReferenceHelper.placeCaretAfterNode(BrowserHelper.detectIE() ? selectedNode.parentElement : selectedNode);
                }

            }

            if (lastKeyCode === 8) { //Backspace

                if (selectedNode.textContent.length <= 1) { //if node is empty
                    removeToken(selectedNode);
                    e.preventDefault();            
                }
                else {
                    let pos = getCurrentCaretPosition(editor);
                    if (pos && pos > 0) {
                        let charToDelete = selectedNode.textContent.charAt(pos - 1);
                        if (charToDelete === '#') {
                            e.preventDefault();
                        }
                    }
                }
            }

            if (lastKeyCode === 27) { //ESC
                removeToken(selectedNode);
                e.preventDefault();  
            }

            searchInputDebounce(selectedNode);            
        }
        else if (lastKeyCode === 9) { //Tab
            OutputReferenceHelper.setAutoFocusOnEditor(false);
            OutputReferenceHelper.moveFocusToWrapper(editor);
        }

    });

    editor.on('NodeChange', (e) => {
        let target = e.element;

        let movingToToken: boolean = OutputReferenceHelper.isOutputReference(target);
        let movingToWrapper: boolean = OutputReferenceHelper.isOutputWrapper(target);

        if (movingToToken || movingToWrapper) {

            if (BrowserHelper.detectIE() && movingToWrapper) {

                OutputReferenceHelper.placeCaretAfterNode(target);

            }
            else {

                currentSearchNode = target;
                OutputReferenceHelper.createTextNodeAfter(editor, BrowserHelper.detectIE() ? currentSearchNode.parentElement : currentSearchNode);

                //this is required to work around a bug that happens if you click out of an incomplete search box shortly after creating it
                //the node change event will fire and the OutputSelect box will still be visible, even though you are not inside a node
                if (!OutputReferenceHelper.getSuppressNodeChange()) {
                    OutputReferenceHelper.outputReferenceSearch(currentSearchNode.textContent, currentSearchNode, editor, editor.plugins.outputReference ? 
                        editor.plugins.outputReference.outputLookupContext : null); 
                }

            }

        }
        else if (currentSearchNode) {

            currentSearchNode = null;
            OutputReferenceHelper.clearOutputReferenceSearch();

        }

    });

    editor.on('init', (e) => {

        OutputReferenceHelper.registerEventHandlers(editor);

    });

    editor.on('undo', (e) => {

        OutputReferenceHelper.registerEventHandlers(editor);

    });

};

tinymce.PluginManager.add('outputReference', outputReferencePlugin);

// sets the cursor to the specified element, ed is the editor instance
// start defines if the cursor is to be set at the start or at the end
// http://stackoverflow.com/questions/4475436/tinymce-move-cursor
let setCursor = (ed, element, start) => {

    var doc = ed.getDoc();
    if (typeof doc.createRange !== "undefined") {
        var range = doc.createRange();
        range.selectNodeContents(element);
        range.collapse(start);
        var win = doc.defaultView || doc.parentWindow;
        var sel = win.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
    } else if (typeof doc.body.createTextRange !== "undefined") {
        var textRange = doc.body.createTextRange();
        textRange.moveToElementText(element);
        textRange.collapse(start);
        textRange.select();
    }
};

let moveToTextNode = (editor, textNode, position) => {
    let rng = tinymce.DOM.createRng();
    rng.setStart(textNode, position);
    rng.setEnd(textNode, position);
    let sel = editor.contentWindow.getSelection();
    sel.removeAllRanges();
    sel.addRange(rng);
};

let getCurrentCaretPosition = (editor) => {
    let sel = editor.contentWindow.getSelection();
    if (sel.rangeCount > 0) {
        var range = sel.getRangeAt(0);
        return range.startOffset;
    }
    return null;
};


// WEBPACK FOOTER //
// ./source/ts/plugins/output-reference/plugin.ts